

import React, { useState, useEffect, useRef } from 'react';
import './PayrollBreakdownTable.css';

const PayrollTable = ({ activePeriod }) => {
  const [payrollTransactions, setPayrollTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [grantList, setGrantList] = useState([]);
  const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [payrollPeriod, setPayrollPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomPeriodPopup, setShowCustomPeriodPopup] = useState(false);
  const fileInputRef = useRef(null);
  const [personnel, setPersonnel] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEditEmployeePopup, setShowEditEmployeePopup] = useState(false);
  const [showInactiveEmployees, setShowInactiveEmployees] = useState(true);
  const [filteredPersonnel, setFilteredPersonnel] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [isUnlocked, setIsUnlocked] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/personnel/list`);
        const data = await response.json();
        setPersonnel(data);
        
        // Create a map of employee data by employee ID for quick lookup
        const empMap = {};
        data.forEach(emp => {
          empMap[emp['Employee Id']] = emp;
        });
        setEmployeeMap(empMap);
      } catch (error) {
        console.error('Error fetching personnel data:', error);
      }
    };
  
    fetchData();
  }, []);

  useEffect(() => {
    if (showInactiveEmployees) {
      setFilteredPersonnel(personnel);
    } else {
      setFilteredPersonnel(personnel.filter(emp => 
        emp['Employment status'] !== 'Inactive' && emp['Employment status'] !== 'inactive'
      ));
    }
  }, [personnel, showInactiveEmployees]);

  useEffect(() => {
    // Check if activePeriod includes a year pattern (e.g., "2023: Q1")
    if (activePeriod && activePeriod.match(/^\d{4}\s-\s/)) {
      // Extract everything after the year and colon
      activePeriod = activePeriod.replace(/^\d{4}\s-\s*/, '');
    }
    setPayrollPeriod(activePeriod || 'all');
  }, [activePeriod]);

  useEffect(() => {
    fetchPayrollTransactions();
    fetchGrantList();
    fetchPersonnel();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [payrollTransactions, payrollPeriod, customStartDate, customEndDate]);

  const fetchPayrollTransactions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/show-all`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setPayrollTransactions(data);
      console.log(data);
    } catch (error) {
      console.error('Error fetching payroll transactions:', error);
    }
  };

  const fetchGrantList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const grantData = await response.json();
      setGrantList(grantData);
    } catch (error) {
      console.error('Error fetching grant list:', error);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/personnel`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const personnelData = await response.json();
      console.log("Personnel Data:", personnelData);
      setPersonnel(personnelData);
    } catch (error) {
      console.error('Error fetching personnel data:', error);
    }
  };

  // In the getGroupedTransactions function, ensure all three transaction types are being handled properly:
  const getGroupedTransactions = (transactions) => {
    const groupedData = [];
    const groups = {};
    
    // First, group transactions by Employee ID and Pay date
    transactions.forEach(transaction => {
      const key = `${transaction['Employee Id']}_${transaction['Pay date']}`;
      
      if (!groups[key]) {
        groups[key] = {
          'Employee Id': transaction['Employee Id'],
          'Name': transaction['Name'],
          'Pay date': transaction['Pay date'],
          'Pay period': transaction['Pay period'],
          'Default Payment': transaction['Default Payment'],
          'Checked': transaction['Checked'],
          'Admin Time': transaction['Admin Time'] || '0',
          'Science Time': transaction['Science Time'] || '0',
        'Direct Deposits': 0,
        'Total Deductions - Employee': 0,
        'Total Deductions - Employer': 0,
        'Total Taxes': 0,
        // Store original transactions for reference
        originalTransactions: []
      };
    }
      
      // Add values to the appropriate columns based on description
      if (transaction['Description'] === 'Direct Deposits') {
        groups[key]['Direct Deposits'] = parseFloat(transaction['Payment']) || 0;
      } else if (transaction['Description'] === 'Total Deductions - Employee') {
        groups[key]['Total Deductions - Employee'] = parseFloat(transaction['Payment']) || 0;
      } else if (transaction['Description'] === 'Total Deductions - Employer') {
        groups[key]['Total Deductions - Employer'] = parseFloat(transaction['Payment']) || 0;
      } else if (transaction['Description'] === 'Total Taxes') {
        groups[key]['Total Taxes'] = parseFloat(transaction['Payment']) || 0;
      }
      
      // If a transaction has Default Payment and the group doesn't, use this one
      if (transaction['Default Payment'] && !groups[key]['Default Payment']) {
        groups[key]['Default Payment'] = transaction['Default Payment'];
      }

      // If any transaction is checked, mark the group as checked
      if (transaction['Checked'] === 'Yes') {
        groups[key]['Checked'] = 'Yes';
      }
      
      // Store the original transaction for reference
      groups[key].originalTransactions.push(transaction);
    });
    
    // Convert groups object to array
    for (const key in groups) {
      groupedData.push(groups[key]);
    }
    
    // Sort by Pay date (newest first) and then by Name
    return groupedData.sort((a, b) => {
      const dateA = new Date(a['Pay date']);
      const dateB = new Date(b['Pay date']);
      if (dateB - dateA === 0) {
        return a['Name'].localeCompare(b['Name']);
      }
      return dateB - dateA;
    });
  };

  const handleEditEmployeeClick = (employee) => {
    let defaultPayment = {};
    if (employee['Default Payment']) {
      try {
        defaultPayment = JSON.parse(employee['Default Payment']);
      } catch (error) {
        console.error('Error parsing Default Payment:', error);
      }
    }
    const employeeType = employee['Type'] || 'Administrator';
    const employmentStatus = employee['Employment status'] || 'Active';
    
    setSelectedEmployee({
      ...employee,
      'Type': employeeType,
      'Employment status': employmentStatus,
      'Default Payment': defaultPayment
    });
    setShowEditEmployeePopup(true);
  };

  const handleEditEmployeeSubmit = async () => {

    console.log("Submitting employee:", selectedEmployee);

    try {
      const totalPercentage = Object.values(selectedEmployee["Default Payment"]).reduce(
        (sum, percentage) => sum + parseFloat(percentage), 0
      );
      
      if (Math.abs(totalPercentage - 100) > 0.01) {
        alert('The total percentage of default payment sources must add up to 100%.');
        return;
      }
      // Make the PUT request to update the employee using the personnel endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/personnel/${selectedEmployee["Employee Id"]}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...selectedEmployee,
        }),
      });

      if (response.ok) {
        // Refresh the personnel list
        fetchPersonnel();
        // Also refresh payroll transactions as they might include updated default payment info
        fetchPayrollTransactions();
        setShowEditEmployeePopup(false);
      } else {
        const errorText = await response.text();
        alert(`Failed to update personnel: ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating personnel:', error);
      alert('Error updating personnel data.');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/import`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          alert('Payroll transactions imported successfully');
          fetchPayrollTransactions();
          fetchPersonnel();
        } else {
          const errorMessage = await response.text();
          alert(`Failed to import payroll transactions: ${errorMessage}`);
        }
      } catch (error) {
        console.error('Error importing payroll transactions:', error);
        alert('Error importing payroll transactions');
      }
    }
  };

  const handleEditDefaultPaymentClick = (groupedTransaction) => {
    let defaultPayment = {};
    
    if (groupedTransaction['Default Payment']) {
      if (typeof groupedTransaction['Default Payment'] === 'string') {
        try {
          defaultPayment = JSON.parse(groupedTransaction['Default Payment']);
        } catch (error) {
          console.error('Error parsing Default Payment:', error);
          defaultPayment = {};
        }
      } else if (typeof groupedTransaction['Default Payment'] === 'object') {
        defaultPayment = groupedTransaction['Default Payment'];
      }
    }
  
    setSelectedTransaction({
      ...groupedTransaction,
      'Default Payment': defaultPayment
    });
    
    setShowEditDefaultPaymentPopup(true);
  };

  const handleEditTransactionClick = (groupedTransaction) => {
    let defaultPayment = {};
    
    if (groupedTransaction['Default Payment']) {
      if (typeof groupedTransaction['Default Payment'] === 'string') {
        try {
          defaultPayment = JSON.parse(groupedTransaction['Default Payment']);
        } catch (error) {
          console.error('Error parsing Default Payment:', error);
          defaultPayment = {};
        }
      } else if (typeof groupedTransaction['Default Payment'] === 'object') {
        defaultPayment = groupedTransaction['Default Payment'];
      }
    }

    // Get admin and science time, ensuring they have default values
    const adminTime = groupedTransaction['Admin Time'] || 
      (employeeMap[groupedTransaction['Employee Id']]?.['Admin Time']) || 0;
    
    const scienceTime = groupedTransaction['Science Time'] || 
      (employeeMap[groupedTransaction['Employee Id']]?.['Science Time']) || 0;

    setSelectedTransaction({
      ...groupedTransaction,
      'Default Payment': defaultPayment,
      'Admin Time': parseInt(adminTime) || 0,
      'Science Time': parseInt(scienceTime) || 0
    });
    
    setShowEditDefaultPaymentPopup(true);
  };

  const handleEditTransactionSubmit = async () => {
    const defaultPayment = selectedTransaction['Default Payment'];
    const adminTime = selectedTransaction['Admin Time'] || 0;
    const scienceTime = selectedTransaction['Science Time'] || 0;
    
    // Ensure defaultPayment is an object
    const defaultPaymentObject = typeof defaultPayment === 'string' ? 
      JSON.parse(defaultPayment) : defaultPayment;
    
    // Validate time allocation
    if (Math.abs((parseInt(adminTime) + parseInt(scienceTime)) - 100) > 0.01) {
      alert('Admin Time (%) + Science Time (%) must equal 100%');
      return;
    }
    
    // Validate payment allocation
    const defaultPaymentKeys = Object.keys(defaultPaymentObject);
    const invalidGrants = defaultPaymentKeys.filter((grant) => !grantList.includes(grant));

    // Check for invalid grants
    if (invalidGrants.length > 0) {
      const confirmOverride = window.confirm(
        `The current Default Payment contains grants (${invalidGrants.join(", ")}) that are not in the current grant list. Editing this record will override the existing items. Do you want to proceed?`
      );

      if (!confirmOverride) {
        return;
      }
    }

    const totalPercentage = Object.values(defaultPaymentObject).reduce(
      (sum, percentage) => sum + parseFloat(percentage), 0
    );
    
    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert('The total percentage of default payment sources must add up to 100%.');
      return;
    }

    try {
      // Get all original transactions from the group
      const transactions = selectedTransaction.originalTransactions;
      let successCount = 0;
      
      // Update each transaction in the group
      for (const transaction of transactions) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            employeeId: transaction['Employee Id'],
            payPeriod: transaction['Pay period'],
            defaultPayment: defaultPaymentObject,
            adminTime: adminTime,
            scienceTime: scienceTime
          }),
        });
      
        if (response.ok) {
          successCount++;
        } else {
          const errorText = await response.text();
          console.error(`Failed to update transaction: ${errorText}`);
        }
      }

      // Show success message and refresh data
      if (successCount > 0) {
        alert(`Updated payment and time allocation for ${successCount} out of ${transactions.length} transactions.`);
        fetchPayrollTransactions();
        setShowEditDefaultPaymentPopup(false);
      } else {
        alert('Failed to update any transactions. Please try again.');
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Error updating transaction data.');
    }
  };


  // Helper function to format Default Payment for display
  const formatDefaultPayment = (defaultPayment) => {
    if (!defaultPayment || defaultPayment === '' || defaultPayment === 'N/A') {
      return '';
    }
  
    if (typeof defaultPayment === 'string') {
      try {
        defaultPayment = JSON.parse(defaultPayment);
      } catch (error) {
        console.error('Error parsing Default Payment:', error);
        return 'Invalid format';
      }
    }
  
    if (typeof defaultPayment === 'object' && defaultPayment !== null) {
      const entries = Object.entries(defaultPayment);
      if (entries.length === 0) {
        return '';
      }
      return entries
        .map(([key, value]) => `${key}: ${value}%`)
        .join(', ');
    }
  
    return 'Invalid format';
  };

  // FOR TESTING PURPOSES:
  // Update handleClearDefaultPayment for grouped data
  const handleClearDefaultPayment = async () => {
    try {
      // Get all original transactions from the group
      const transactions = selectedTransaction.originalTransactions;
      console.log("transactions to clear:", transactions);
      let successCount = 0;
      
      // Clear default payment for each transaction
      for (const transaction of transactions) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/defaultPayment`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            employeeId: transaction['Employee Id'],
            payPeriod: transaction['Pay period'],
            defaultPayment: null,
          }),
        });
      
        if (response.ok) {
          successCount++;
        } else {
          const errorText = await response.text();
          console.error(`Failed to clear transaction: ${errorText}`);
        }
      }
  
      // Show success message and refresh data
      if (successCount > 0) {
        // alert(`Cleared default payment for ${successCount} out of ${transactions.length} transactions.`);
        alert('Cleared default payment for all transactions in this group.');
        fetchPayrollTransactions();
        setShowEditDefaultPaymentPopup(false);
      } else {
        alert('Failed to clear any transactions. Please try again.');
      }
    } catch (error) {
      console.error('Error clearing default payment:', error);
      alert('Error clearing default payment.');
    }
  };
  

  // Update handleEditDefaultPaymentSubmit to apply changes to all transactions in the group
  // const handleEditDefaultPaymentSubmit = async () => {
  //   const defaultPayment = selectedTransaction['Default Payment'];
    
  //   // Ensure defaultPayment is an object
  //   const defaultPaymentObject = typeof defaultPayment === 'string' ? 
  //     JSON.parse(defaultPayment) : defaultPayment;
    
  //   const defaultPaymentKeys = Object.keys(defaultPaymentObject);
  //   const invalidGrants = defaultPaymentKeys.filter((grant) => !grantList.includes(grant));
  
  //   // Check for invalid grants
  //   if (invalidGrants.length > 0) {
  //     const confirmOverride = window.confirm(
  //       `The current Default Payment contains grants (${invalidGrants.join(", ")}) that are not in the current grant list. Editing this record will override the existing items. Do you want to proceed?`
  //     );
  
  //     if (!confirmOverride) {
  //       return;
  //     }
  //   }
  
  //   const totalPercentage = Object.values(defaultPaymentObject).reduce(
  //     (sum, percentage) => sum + parseFloat(percentage), 0
  //   );
    
  //   if (Math.abs(totalPercentage - 100) > 0.01) {
  //     alert('The total percentage of default payment sources must add up to 100%.');
  //     return;
  //   }
  
  //   try {
  //     // Get all original transactions from the group
  //     const transactions = selectedTransaction.originalTransactions;
  //     let successCount = 0;
      
  //     // Update each transaction in the group
  //     for (const transaction of transactions) {
  //       const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/defaultPayment`, {
  //         method: 'PUT',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify({
  //           employeeId: transaction['Employee Id'],
  //           payPeriod: transaction['Pay period'],
  //           defaultPayment: defaultPaymentObject,
  //         }),
  //       });
      
  //       if (response.ok) {
  //         successCount++;
  //       } else {
  //         const errorText = await response.text();
  //         console.error(`Failed to update transaction: ${errorText}`);
  //       }
  //     }
  
  //     // Show success message and refresh data
  //     if (successCount > 0) {
  //       alert(`Updated default payment for ${successCount} out of ${transactions.length} transactions.`);
  //       fetchPayrollTransactions();
  //       setShowEditDefaultPaymentPopup(false);
  //     } else {
  //       alert('Failed to update any transactions. Please try again.');
  //     }
  //   } catch (error) {
  //     console.error('Error updating default payment:', error);
  //     alert('Error updating default payment.');
  //   }
  // };
  
  
  const handleTransactionSelect = (groupedTransaction) => {
    const transactionKey = `${groupedTransaction['Employee Id']}_${groupedTransaction['Pay date']}`;
    const newSelectedTransactions = new Set(selectedTransactions);
    
    if (selectedTransactions.has(transactionKey)) {
      newSelectedTransactions.delete(transactionKey);
    } else {
      newSelectedTransactions.add(transactionKey);
    }
    
    setSelectedTransactions(newSelectedTransactions);
  };

  const handleSubmitSelected = async () => {
    if (selectedTransactions.size === 0) {
      alert('Please select transactions to submit.');
      return;
    }

    try {
      let successCount = 0;
      let failedCount = 0;

      // Find and process each selected transaction
      for (const transactionKey of selectedTransactions) {
        const groupedTransaction = filteredTransactions.find(
          t => `${t['Employee Id']}_${t['Pay date']}` === transactionKey
        );
        
        if (groupedTransaction) {
          // Update each transaction in the group to confirmed status
          for (const transaction of groupedTransaction.originalTransactions) {
            try {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/check/${transaction['Pay period']}/${transaction['Employee Id']}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ Checked: true }),
              });
              
              if (response.ok) {
                successCount++;
              } else {
                failedCount++;
              }
            } catch (error) {
              console.error('Error confirming transaction:', error);
              failedCount++;
            }
          }
        }
      }

      // Clear selected transactions and refresh data
      setSelectedTransactions(new Set());
      fetchPayrollTransactions();
      
      alert(`Successfully submitted ${successCount} transactions. ${failedCount} failed.`);
    } catch (error) {
      console.error('Error submitting transactions:', error);
      alert('Error submitting selected transactions.');
    }
  };

  const handleUnlockAll = async () => {
    const confirmUnlock = window.confirm(
      'Are you sure you want to unlock all confirmed transactions? This will allow them to be edited, and they will remain selected for easy resubmission.'
    );
    
    if (!confirmUnlock) return;

    try {
      let successCount = 0;
      let failedCount = 0;

      // Get all confirmed transactions in the current filtered view
      const confirmedTransactions = filteredTransactions.filter(t => t.Checked === 'Yes');
      const newSelectedTransactions = new Set();
      
      for (const groupedTransaction of confirmedTransactions) {
        // Add this transaction to the selected set so it remains checked
        const transactionKey = `${groupedTransaction['Employee Id']}_${groupedTransaction['Pay date']}`;
        newSelectedTransactions.add(transactionKey);
        
        // Update each transaction in the group to unconfirmed status
        for (const transaction of groupedTransaction.originalTransactions) {
          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/check/${transaction['Pay period']}/${transaction['Employee Id']}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ Checked: false }),
            });
            
            if (response.ok) {
              successCount++;
            } else {
              failedCount++;
            }
          } catch (error) {
            console.error('Error unlocking transaction:', error);
            failedCount++;
          }
        }
      }

      // Set the previously confirmed transactions as selected
      setSelectedTransactions(newSelectedTransactions);
      fetchPayrollTransactions();
      setIsUnlocked(true);
      
      alert(`Successfully unlocked ${successCount} transactions. ${failedCount} failed. Previously confirmed transactions remain selected for easy resubmission.`);
    } catch (error) {
      console.error('Error unlocking transactions:', error);
      alert('Error unlocking transactions.');
    }
  };



  const handlePayrollPeriodChange = (event) => {
    const selectedPeriod = event.target.value;
    setPayrollPeriod(selectedPeriod);
    if (selectedPeriod === 'custom') {
      setShowCustomPeriodPopup(true);
    } else {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const handleCustomPeriodSubmit = () => {
    setShowCustomPeriodPopup(false);
    setPayrollPeriod('custom');
  };

  const filterTransactions = () => {
    let filtered = payrollTransactions;

    const currentDate = new Date();
    let startDate, endDate;

    switch (payrollPeriod) {
      case 'lastYear':
        startDate = new Date(currentDate.getFullYear() - 1, 0, 1);
        endDate = new Date(currentDate.getFullYear() - 1, 11, 31);
        break;
      case 'lastQuarter':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case 'lastMonth':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case 'custom':
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        break;
      default:
        if (!isNaN(parseInt(payrollPeriod))) {
          startDate = new Date(parseInt(payrollPeriod), 0, 1);
          endDate = new Date(parseInt(payrollPeriod), 11, 31);
        } else if (payrollPeriod !== 'all') {
          // try to match exact pay period
          filtered = payrollTransactions.filter(t => t['Pay period'] === payrollPeriod);
        }
    }

    if (startDate && endDate) {
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t['Pay date']);
        console.log(transactionDate, startDate, endDate);
        return transactionDate >= startDate && transactionDate <= endDate;
      });
    }

    setFilteredTransactions(getGroupedTransactions(filtered));
  };

  const getPayrollPeriodOptions = () => {
    const years = [...new Set(payrollTransactions.map(t => new Date(t['Pay date']).getFullYear()))];
    const payPeriods = [...new Set(payrollTransactions.map(t => t['Pay period']))];

    console.log(payPeriods);

    return [
      { value: 'all', label: 'All' },
      ...years.map(year => ({ value: year.toString(), label: year.toString() })),
      { value: 'lastYear', label: 'Last Year' },
      { value: 'lastQuarter', label: 'Last Quarter' },
      { value: 'lastMonth', label: 'Last Month' },
      { value: 'custom', label: 'Custom Period' },
      ...payPeriods.map(period => ({ value: period, label: period })),
    ];
  };

  
  const handleAutofillDefaultPayments = async () => {
    try {
      // Start with the currently filtered transactions instead of all payroll transactions
      const currentlyDisplayed = filteredTransactions;
      
      // Find transactions without default payment or with empty default payment
      // But only from the currently displayed transactions
      const unfilled = [];
      
      // Go through each grouped transaction and look at all original transactions within it
      currentlyDisplayed.forEach(groupedTransaction => {
        const originalTransactions = groupedTransaction.originalTransactions || [];
        
        // Check if the grouped transaction needs default payment
        const needsDefaultPayment = !groupedTransaction['Default Payment'] || 
          groupedTransaction['Default Payment'] === '' || 
          groupedTransaction['Default Payment'] === 'N/A' ||
          (typeof groupedTransaction['Default Payment'] === 'object' && 
            Object.keys(groupedTransaction['Default Payment']).length === 0);
        
        if (needsDefaultPayment) {
          // Add all original transactions to the unfilled list
          unfilled.push(...originalTransactions);
        }
      });
  
      if (unfilled.length === 0) {
        alert('No unfilled default payments found in the current period.');
        return;
      }
  
      // Group by employee ID for efficiency
      const employeeGroups = {};
      unfilled.forEach(transaction => {
        const id = transaction['Employee Id'];
        if (!employeeGroups[id]) {
          employeeGroups[id] = [];
        }
        employeeGroups[id].push(transaction);
      });
  
      // For each employee ID, find the corresponding employee and fill in their default payment
      let updatedCount = 0;
      let successCount = 0;
      let failedCount = 0;
  
      // Process each employee one by one
      for (const [employeeId, transactions] of Object.entries(employeeGroups)) {
        // Find the employee in personnel data
        const employee = personnel.find(p => p['Employee Id'] === employeeId);
        
        if (employee && employee['Default Payment']) {
          // Get the default payment (ensure it's an object)
          let defaultPayment;
          if (typeof employee['Default Payment'] === 'string') {
            try {
              defaultPayment = JSON.parse(employee['Default Payment']);
            } catch (error) {
              console.error(`Error parsing Default Payment for employee ${employeeId}:`, error);
              continue;
            }
          } else {
            defaultPayment = employee['Default Payment'];
          }
  
          // Make sure defaultPayment is a non-empty object
          if (typeof defaultPayment !== 'object' || defaultPayment === null || Object.keys(defaultPayment).length === 0) {
            console.warn(`Skipping employee ${employeeId} due to invalid Default Payment format`);
            continue;
          }
  
          // Update each transaction for this employee
          for (const transaction of transactions) {
            try {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/defaultPayment`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  employeeId: transaction['Employee Id'],
                  payPeriod: transaction['Pay period'],
                  defaultPayment: defaultPayment,
                }),
              });
  
              if (response.ok) {
                successCount++;
              } else {
                console.error(`Failed to update transaction for employee ${employeeId}: ${await response.text()}`);
                failedCount++;
              }
              updatedCount++;
            } catch (error) {
              console.error(`Error updating transaction for employee ${employeeId}:`, error);
              failedCount++;
              updatedCount++;
            }
          }
        }
      }
      
      // Refresh data
      fetchPayrollTransactions();
      
      // Update the button text to reflect what was done
      const periodText = payrollPeriod === 'all' ? 'all periods' : `the '${payrollPeriod}' period`;
      
      // Show results
      alert(`Updated default payments for ${successCount} transactions in ${periodText}. ${failedCount} transactions failed. (Total eligible: ${updatedCount})`);
      
    } catch (error) {
      console.error('Error auto-filling default payments:', error);
      alert('Error auto-filling default payments: ' + error.message);
    }
  };

  // Updated autofill function to handle both payment and time allocation
  const handleAutofillPaymentAndTime = async () => {
    try {
      // Start with the currently filtered transactions
      const currentlyDisplayed = filteredTransactions;
      
      // Find transactions without default payment or proper time allocation
      const unfilled = [];
      
      // Go through each grouped transaction and look at all original transactions within it
      currentlyDisplayed.forEach(groupedTransaction => {
        const originalTransactions = groupedTransaction.originalTransactions || [];
        
        // Check if the grouped transaction needs default payment or time allocation
        const needsDefaultPayment = !groupedTransaction['Default Payment'] || 
          groupedTransaction['Default Payment'] === '' || 
          groupedTransaction['Default Payment'] === 'N/A' ||
          (typeof groupedTransaction['Default Payment'] === 'object' && 
            Object.keys(groupedTransaction['Default Payment']).length === 0);
        
        const needsTimeAllocation = !groupedTransaction['Admin Time'] || 
          !groupedTransaction['Science Time'];
        
        if (needsDefaultPayment || needsTimeAllocation) {
          // Add all original transactions to the unfilled list
          unfilled.push(...originalTransactions);
        }
      });

      if (unfilled.length === 0) {
        alert('No unfilled payments or time allocations found in the current period.');
        return;
      }

      // Group by employee ID for efficiency
      const employeeGroups = {};
      unfilled.forEach(transaction => {
        const id = transaction['Employee Id'];
        if (!employeeGroups[id]) {
          employeeGroups[id] = [];
        }
        employeeGroups[id].push(transaction);
      });

      // For each employee ID, find the corresponding employee and fill in their default payment
      let updatedCount = 0;
      let successCount = 0;
      let failedCount = 0;

      // Process each employee one by one
      for (const [employeeId, transactions] of Object.entries(employeeGroups)) {
        // Find the employee in personnel data
        const employee = personnel.find(p => p['Employee Id'] === employeeId);
        
        if (employee) {
          // Get the default payment (ensure it's an object)
          let defaultPayment = null;
          if (employee['Default Payment']) {
            if (typeof employee['Default Payment'] === 'string') {
              try {
                defaultPayment = JSON.parse(employee['Default Payment']);
              } catch (error) {
                console.error(`Error parsing Default Payment for employee ${employeeId}:`, error);
                defaultPayment = null;
              }
            } else {
              defaultPayment = employee['Default Payment'];
            }
          }

          // Get time allocation
          const adminTime = employee['Admin Time'] || (employee['Type'] === 'Administrator' ? 100 : 0);
          const scienceTime = employee['Science Time'] || (employee['Type'] === 'Scientist' ? 100 : 0);

          // Update each transaction for this employee
          for (const transaction of transactions) {
            try {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/update`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  employeeId: transaction['Employee Id'],
                  payPeriod: transaction['Pay period'],
                  defaultPayment: defaultPayment,
                  adminTime: adminTime,
                  scienceTime: scienceTime
                }),
              });

              if (response.ok) {
                successCount++;
              } else {
                console.error(`Failed to update transaction for employee ${employeeId}: ${await response.text()}`);
                failedCount++;
              }
              updatedCount++;
            } catch (error) {
              console.error(`Error updating transaction for employee ${employeeId}:`, error);
              failedCount++;
              updatedCount++;
            }
          }
        }
      }
      
      // Refresh data
      fetchPayrollTransactions();
      
      // Update the button text to reflect what was done
      const periodText = payrollPeriod === 'all' ? 'all periods' : `the '${payrollPeriod}' period`;
      
      // Show results
      alert(`Updated payment and time allocation for ${successCount} transactions in ${periodText}. ${failedCount} transactions failed. (Total eligible: ${updatedCount})`);
      
    } catch (error) {
      console.error('Error auto-filling data:', error);
      alert('Error auto-filling payment and time data: ' + error.message);
    }
  };

  return (
    <div className="payroll-table-container">
      {/* <h2>Employees</h2>
      <div className="employee-controls">
        <button 
          onClick={() => setShowInactiveEmployees(!showInactiveEmployees)}
          className={showInactiveEmployees ? "active" : ""}
        >
          {showInactiveEmployees ? "Hide Inactive Employees" : "Show All Employees"}
        </button>
      </div> */}
      {/* <table className="employee-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Employment Status</th>
            <th>Default Type</th>
            <th>Default Payment (%)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredPersonnel.map((employee) => (
            <tr key={employee['Employee Id']}>
              <td>{employee['Name']}</td>
              <td>{employee['Employment status']}</td>
              <td>
                {employee['Type']}
                {employee['Type'] === 'Administrator and Scientist' && (
                  <span className="time-split">
                    {` (Admin: ${employee['Admin Time'] || 0}%, Science: ${employee['Science Time'] || 0}%)`}
                  </span>
                )}
              </td>
              <td>{formatDefaultPayment(employee['Default Payment'])}</td>
              <td>
                <button onClick={() => handleEditEmployeeClick(employee)}>
                  Edit Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table> */}

      <h2>Payroll Transactions</h2>
      <div className="payroll-controls">
        {activePeriod === 'all' && <div>
          <label>Payroll Period:</label>
          <select value={payrollPeriod} onChange={handlePayrollPeriodChange}>
            {getPayrollPeriodOptions().map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>}
        
        <div className="payroll-actions">
          <button 
            onClick={handleSubmitSelected}
            disabled={selectedTransactions.size === 0}
            className="submit-button"
          >
            Approve Selected ({selectedTransactions.size})
          </button>
          
          <button 
            onClick={handleUnlockAll}
            className="unlock-button"
          >
            Unlock All Confirmed
          </button>
        </div>
        {/* <button onClick={handleAutofillPaymentAndTime}>Autofill Payment & Time (for current pay period)</button> */}
      </div>
      <table className="payroll-table">
        <thead>
          <tr>
            {payrollPeriod === 'all' && <th>Pay period</th>}
            {payrollPeriod === 'all' && <th>Pay date</th>}
            <th>Name</th>
            <th>Direct Deposits</th>
            <th>Total Deductions - Emp</th>
            <th>Total Deductions - Empr</th>
            <th>Total Taxes</th>
            <th>Admin Time (%)</th>
            <th>Science Time (%)</th>
            <th>Payment (%)</th>
            <th>Edit</th>
            <th>Approve</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
        {filteredTransactions.map((groupedTransaction, index) => (
          <tr key={`${groupedTransaction['Employee Id']}_${groupedTransaction['Pay date']}`}>
            {payrollPeriod === 'all' && <td>{groupedTransaction['Pay period']}</td>}
            {payrollPeriod === 'all' && <td>{groupedTransaction['Pay date']}</td>}
            <td>{groupedTransaction['Name']}</td>
            <td>${groupedTransaction['Direct Deposits'].toFixed(2)}</td>
            <td>${groupedTransaction['Total Deductions - Employee'].toFixed(2)}</td>
            <td>${groupedTransaction['Total Deductions - Employer'].toFixed(2)}</td>
            <td>${groupedTransaction['Total Taxes'].toFixed(2)}</td>
            <td>
              {groupedTransaction['Admin Time'] || 
                (employeeMap[groupedTransaction['Employee Id']] && 
                employeeMap[groupedTransaction['Employee Id']]['Admin Time']) || '0'}%
            </td>
            <td>
              {groupedTransaction['Science Time'] || 
                (employeeMap[groupedTransaction['Employee Id']] && 
                employeeMap[groupedTransaction['Employee Id']]['Science Time']) || '0'}%
            </td>
            <td>{formatDefaultPayment(groupedTransaction['Default Payment'])}</td>
            <td>
              <button 
                onClick={() => handleEditTransactionClick(groupedTransaction)}
                disabled={groupedTransaction['Checked'] === 'Yes'}
                className={groupedTransaction['Checked'] === 'Yes' ? 'disabled-button' : ''}
              >
                {groupedTransaction['Checked'] === 'Yes' ? 'Locked' : 'Edit'}
              </button>
            </td>
            <td>
              {groupedTransaction['Checked'] === 'Yes' ? (
                <span className="confirmed-indicator">Approved</span>
              ) : (
                <input
                  type="checkbox"
                  checked={selectedTransactions.has(`${groupedTransaction['Employee Id']}_${groupedTransaction['Pay date']}`)}
                  onChange={() => handleTransactionSelect(groupedTransaction)}
                />
              )}
            </td>
            <td>
              <span className={`status-indicator ${groupedTransaction['Checked'] === 'Yes' ? 'confirmed' : 'pending'}`}>
                {groupedTransaction['Checked'] === 'Yes' ? 'Confirmed' : 'Pending'}
              </span>
            </td>
          </tr>
        ))}
        </tbody>
      </table>

      <div className="totals-display">
        <div>
          <strong>Total Direct Deposits:</strong> 
          <span className="total-number">
            {filteredTransactions.reduce((sum, transaction) => {
              return sum + (parseFloat(transaction['Direct Deposits']) || 0);
            }, 0).toFixed(2)}
          </span>
        </div>
        <div>
          <strong>Total Deductions - Employee:</strong> 
          <span className="total-number">
            {filteredTransactions.reduce((sum, transaction) => {
              return sum + (parseFloat(transaction['Total Deductions - Employee']) || 0);
            }, 0).toFixed(2)}
          </span>
        </div>
        <div>
          <strong>Total Deductions - Employer:</strong> 
          <span className="total-number">
            {filteredTransactions.reduce((sum, transaction) => {
              return sum + (parseFloat(transaction['Total Deductions - Employer']) || 0);
            }, 0).toFixed(2)}
          </span>
        </div>
        <div>
          <strong>Total Taxes:</strong> 
          <span className="total-number">
            {filteredTransactions.reduce((sum, transaction) => {
              return sum + (parseFloat(transaction['Total Taxes']) || 0);
            }, 0).toFixed(2)}
          </span>
        </div>
      </div>

      {showEditDefaultPaymentPopup && (
        <div className="popup">
          <h3>Edit Payment & Time Allocation</h3>

          {/* Add Time Allocation Section */}
          <div className="time-allocation-section">
            <h4>Time Allocation</h4>
            <div className="time-allocation-inputs">
              <div>
                <label>Admin Time (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={selectedTransaction['Admin Time'] || 0}
                  onChange={(e) => {
                    const adminTime = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                    setSelectedTransaction({
                      ...selectedTransaction,
                      'Admin Time': adminTime,
                      'Science Time': 100 - adminTime
                    });
                  }}
                />
              </div>
              <div>
                <label>Science Time (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={selectedTransaction['Science Time'] || 0}
                  onChange={(e) => {
                    const scienceTime = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                    setSelectedTransaction({
                      ...selectedTransaction,
                      'Science Time': scienceTime,
                      'Admin Time': 100 - scienceTime
                    });
                  }}
                />
              </div>
            </div>
            <p className="note">Note: Admin Time + Science Time must equal 100%</p>
          </div>

          <h4>Payment Allocation</h4>
          <p>Please fill in the percentage of each payment source. The sum of all payment source percentages should be 100%</p>
          {grantList.map((grant) => (
            <div key={grant}>
              <label>{grant}</label>
              <select
                value={selectedTransaction['Default Payment'][grant] || 0}
                onChange={(e) => {
                  const newPercentage = parseInt(e.target.value, 10) || 0;
                  setSelectedTransaction({
                    ...selectedTransaction,
                    'Default Payment': {
                      ...selectedTransaction['Default Payment'],
                      [grant]: newPercentage,
                    },
                  });
                }}
              >
                {Array.from({ length: 101 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          ))}
          <div className="popup-actions">
            <button onClick={handleEditTransactionSubmit}>Save</button>
            <button 
              onClick={handleClearDefaultPayment}
              className="clear-button"
            >
              Clear Payment
            </button>
            <button onClick={() => setShowEditDefaultPaymentPopup(false)}>Cancel</button>
          </div>
        </div>
      )}
      {showEditEmployeePopup && (
        <div className="popup">
          <h3>Edit Employee Details</h3>
          <div>
            <label>Employment Status:</label>
            <select
              value={selectedEmployee["Employment status"] || 'Active'}
              onChange={(e) => setSelectedEmployee({
                ...selectedEmployee,
                "Employment status": e.target.value
              })}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label>Employee Type:</label>
            <select
              value={selectedEmployee["Type"] || 'Administrator'}
              onChange={(e) => setSelectedEmployee({
                ...selectedEmployee,
                "Type": e.target.value
              })}
            >
              <option value="Administrator">Administrator</option>
              <option value="Scientist">Scientist</option>
              <option value="Administrator and Scientist">Administrator and Scientist</option>
            </select>
          </div>

          {selectedEmployee["Type"] === 'Administrator and Scientist' && 
            <div>
              <h3>Edit Time Allocation</h3>
              <div>
                <label>Admin Time (%)</label>
                <select
                  value={selectedEmployee['Admin Time']}
                  onChange={(e) => setSelectedEmployee({ ...selectedEmployee, 'Admin Time': parseInt(e.target.value) })}
                >
                  {Array.from({ length: 101 }, (_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Science Time (%)</label>
                <select
                  value={selectedEmployee['Science Time']}
                  onChange={(e) => setSelectedEmployee({ ...selectedEmployee, 'Science Time': parseInt(e.target.value) })}
                >
                  {Array.from({ length: 101 }, (_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
            </div>
          }
          
          <h4>Default Payment Split</h4>
          <p>Please fill in the percentage of each payment source. The sum of all payment source percentages should be 100%</p>
          {grantList.map((grant) => (
            <div key={grant}>
              <label>{grant}</label>
              <select
                value={selectedEmployee["Default Payment"][grant] || 0}
                onChange={(e) => {
                  const newPercentage = parseInt(e.target.value, 10) || 0;
                  setSelectedEmployee({
                    ...selectedEmployee,
                    'Default Payment': {
                      ...selectedEmployee["Default Payment"],
                      [grant]: newPercentage,
                    },
                  });
                }}
              >
                {Array.from({ length: 101 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          ))}
          <button onClick={handleEditEmployeeSubmit}>Save</button>
          <button onClick={() => setShowEditEmployeePopup(false)}>Cancel</button>
        </div>
      )}

      {showCustomPeriodPopup && (
        <div className="popup">
          <h3>Select Custom Period</h3>
          <div>
            <label>Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
            />
          </div>
          <button onClick={handleCustomPeriodSubmit}>Apply</button>
          <button onClick={() => setShowCustomPeriodPopup(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default PayrollTable;